import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, Clock3, Cpu, DollarSign,
  Link2, MessageCircle, RefreshCw, ShieldCheck, Sparkles, Target, Zap,
} from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Card, Spinner, useToast } from "@/components/earnos/kit";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/theme";

const goalExamples = [
  {
    key: "first-sale",
    label: "Find my first paying customer",
    prompt: "Help me earn my first legitimate sale. Discover a buyer problem using current verifiable research where available, build a practical service offer I can deliver, and use only authorized connected services for any outside action. Ask for approval before sending or publishing. Show separately what is prepared, what was actually done, and whether a real payment was independently verified. Do not invent buyers or earnings.",
    icon: Target,
  },
  {
    key: "business",
    label: "Build an income business",
    prompt: "Help me build and operate a legitimate income-generating service business fitted to my existing EARNOS income engine and constraints. Validate demand, create an offer, prepare the customer workflow, and execute approved external steps only with connected tools. Separate delivered work, published offers, customer responses, and verified sales. Stop and explain any missing required connection; never pretend that a generated document earns money.",
    icon: Zap,
  },
  {
    key: "website",
    label: "Build and launch my offer website",
    prompt: "Create a real mobile-friendly website for a legitimate offer I can deliver, not a PDF or mockup. Use build_offer_website to produce a responsive HTML/CSS page with an accessible offer, specific benefits, realistic seller-provided price and an HTTPS call to action. Ask me only for genuinely missing service/price/CTA details. Verify the private page, then use publish_offer_website with exact approval to make it publicly accessible. Verify the actual public HTTP page. This proves publication, not a customer purchase or income.",
    icon: Sparkles,
  },
  {
    key: "improve",
    label: "Grow my existing income",
    prompt: "Review my existing EARNOS income engine, customer leads, pricing, and reported receipts. Suggest the highest-value lawful next move based on evidence, do the preparatory work with available tools, then ask for exact approval of any connected-account action. Never assume reported earnings were independently verified; show what still needs validation.",
    icon: Sparkles,
  },
];

const attentionStates = new Set(["awaiting_approval", "needs_input", "needs_connection", "blocked"]);
const activeStates = new Set(["queued", "planning", "running", "verifying"]);
const statusLabel = (state) => (state || "queued").replaceAll("_", " ");
const short = (text, length=130) => String(text || "").length > length ? String(text).slice(0,length-1) + "…" : text;

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [dashboard, setDashboard] = useState(null);
  const [missions, setMissions] = useState([]);
  const [overview, setOverview] = useState(null);
  const [connections, setConnections] = useState([]);
  const [goal, setGoal] = useState("");
  const [chosen, setChosen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadWarning, setLoadWarning] = useState("");

  const refresh = useCallback(async () => {
    const result = await Promise.allSettled([
      apiFetch("/dashboard"),
      apiFetch("/mission/missions"),
      apiFetch("/mission/overview"),
      apiFetch("/connections"),
    ]);
    if (result[0].status === "fulfilled") setDashboard(result[0].value);
    if (result[1].status === "fulfilled")
      setMissions(Array.isArray(result[1].value) ? result[1].value : []);
    if (result[2].status === "fulfilled") setOverview(result[2].value);
    if (result[3].status === "fulfilled")
      setConnections(Array.isArray(result[3].value) ? result[3].value : []);
    setLoadWarning(result.some(x => x.status === "rejected")
      ? "Some account information could not be loaded. " + result.filter(x => x.status === "rejected").map(x => x.reason?.message || "Service unavailable").join("; ")
      : "");
    setLoading(false);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const start = () => {
    const request = goal.trim();
    if (request.length < 8) {
      toast.show("Tell EARNOS what you want it to accomplish.", "error");
      return;
    }
    navigate("/mission", { state: { missionGoal: request.slice(0, 2000) } });
  };
  const choose = (option) => {
    setChosen(option.key);
    setGoal(option.prompt);
  };

  const needYou = missions.filter(m => attentionStates.has(m.state));
  const active = missions.filter(m => activeStates.has(m.state));
  const recent = missions.slice(0, 4);
  const connected = connections.filter(c => c.connected);
  const received = dashboard?.received;
  const target = dashboard?.goal_amount;
  const move = dashboard?.today_move;

  return (
    <div className="animate-fade-in space-y-6" data-testid="earnos-income-command-center">
      {user?.demo_mode ? <p role="status" className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-sm text-amber-200">Demo data is enabled. Example income and customer activity are not real earnings. Manage demo mode in Settings.</p> : null}
      <header className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-light">EARNOS · Income command center</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-ink mt-1">What should EARNOS get done?</h1>
          <p className="text-sm text-ink-2 mt-2">Choose your outcome. EARNOS handles supported work and asks before taking actions in your accounts.</p>
        </div>
        <button type="button" onClick={refresh} aria-label="Refresh live account status"
          className="p-3 rounded-xl border border-line bg-surface-2 text-ink hover:border-brand"><RefreshCw size={18}/></button>
      </header>
      {loadWarning ? <div role="alert" className="rounded-xl border border-amber-400/30 p-3 text-sm text-amber-200"><p>{loadWarning}</p><button type="button" onClick={refresh} className="mt-2 underline">Retry loading account information</button></div> : null}

      <section className="relative overflow-hidden rounded-3xl border border-brand/40 bg-gradient-to-br from-surface-2 via-surface-2 to-brand/15 p-5 sm:p-8 space-y-5">
        <div className="absolute right-3 top-3 opacity-10 pointer-events-none"><Cpu size={110}/></div>
        <div className="relative">
          <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-light"><Sparkles size={17}/> Your AI income operator</p>
          <h2 className="mt-2 text-2xl sm:text-3xl text-ink font-extrabold max-w-xl leading-tight">Describe the result. Let EARNOS plan the work.</h2>
          <p className="text-sm text-ink-2 mt-2 max-w-xl">Research, build, prepare and—when supported and approved—execute. See real work, blockers and evidence in one place.</p>
        </div>
        <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Choose an income goal">
          {goalExamples.map(item => {
            const Icon = item.icon;
            return <button key={item.key} type="button" aria-pressed={chosen === item.key}
              onClick={() => choose(item)}
              className={"flex gap-2 items-start text-left rounded-2xl border p-3 sm:p-4 transition-colors " +
                (chosen === item.key ? "border-brand bg-brand/20 text-ink" : "border-line bg-surface-3 text-ink hover:border-brand/50")}>
              <Icon size={19} className="text-brand-light shrink-0 mt-0.5"/>
              <span className="text-sm font-bold leading-snug">{item.label}</span>
            </button>;
          })}
        </div>
        <form onSubmit={e => {e.preventDefault(); start();}} className="relative space-y-3">
          <label htmlFor="home-income-goal" className="block font-semibold text-sm text-ink">Or tell EARNOS your goal in your own words</label>
          <textarea id="home-income-goal" value={goal} rows={3} maxLength={2000}
            onChange={e => { setGoal(e.target.value); setChosen(null); }}
            placeholder="I want to start a legitimate service business, get my first customer, and show me what actually earns money…"
            className="w-full rounded-2xl bg-surface-3 border border-line p-4 text-ink placeholder:text-ink-2 focus:border-brand outline-none resize-y"/>
          <div className="flex flex-col sm:flex-row gap-2 justify-between sm:items-center">
            <span className="text-xs text-ink-2 flex items-center gap-1.5"><ShieldCheck size={15}/> You approve external actions. Income is never guaranteed.</span>
            <button type="submit" data-testid="home-start-income-goal" disabled={goal.trim().length < 8}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand text-white px-6 h-12 font-bold disabled:opacity-40 hover:brightness-110">Start my goal <ArrowRight size={18}/></button>
          </div>
        </form>
      </section>

      <div className="grid sm:grid-cols-3 gap-3">
        <button type="button" onClick={() => navigate("/chat")}
          className="text-left flex gap-3 items-center rounded-2xl border border-line bg-surface-2 p-4 hover:border-brand">
          <MessageCircle className="text-brand-light" size={24}/><span><strong className="text-sm text-ink">Chat with EARNOS</strong><span className="block text-xs text-ink-2 mt-1">Ask, explore and turn questions into work</span></span>
        </button>
        <button type="button" onClick={() => navigate("/connections")}
          className="text-left flex gap-3 items-center rounded-2xl border border-line bg-surface-2 p-4 hover:border-brand">
          <Link2 className="text-brand-light" size={24}/><span><strong className="text-sm text-ink">Connect my accounts</strong><span className="block text-xs text-ink-2 mt-1">{connected.length} connected · only supported services</span></span>
        </button>
        <button type="button" onClick={() => navigate("/mission")}
          className="text-left flex gap-3 items-center rounded-2xl border border-line bg-surface-2 p-4 hover:border-brand">
          <Cpu className="text-brand-light" size={24}/><span><strong className="text-sm text-ink">See work getting done</strong><span className="block text-xs text-ink-2 mt-1">{active.length} active · {needYou.length} need you</span></span>
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3 !p-5">
          <div className="flex justify-between items-start gap-3">
            <div>
              <p className="font-semibold text-sm text-ink-2">Reported received income</p>
              <p className="text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">
                {loading ? "…" : received == null ? "Unavailable" : money(received)}
              </p>
              <p className="text-xs text-ink-2 mt-2">Customer records only. Not independently verified processor payouts.</p>
            </div>
            <DollarSign className="text-brand-light shrink-0" size={27}/>
          </div>
          <div className="mt-4 pt-4 border-t border-line flex flex-wrap justify-between gap-2 text-sm text-ink-2">
            <span>Monthly target: <strong className="text-ink">{target ? money(target) : "Not set"}</strong></span>
            <button type="button" className="font-bold text-brand-light inline-flex items-center gap-1" onClick={() => navigate("/earnings")}>View income records <ArrowUpRight size={15}/></button>
          </div>
        </Card>
        <Card className="lg:col-span-2 !p-5">
          <p className="text-sm text-ink-2 font-semibold">Real work status</p>
          <div className="grid grid-cols-2 mt-4 gap-3">
            <div><p className="text-3xl font-extrabold text-ink">{loading ? "…" : overview?.running ?? active.length}</p><p className="text-xs text-ink-2">Running</p></div>
            <div><p className="text-3xl font-extrabold text-ink">{loading ? "…" : overview?.needs_you ?? needYou.length}</p><p className="text-xs text-ink-2">Need approval or help</p></div>
          </div>
          <button type="button" className="text-sm text-brand-light font-bold mt-4 inline-flex items-center gap-1" onClick={() => navigate("/mission")}>Open Mission Control <ArrowRight size={15}/></button>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="flex justify-between items-center gap-2">
            <h2 className="text-lg font-bold text-ink">Needs you</h2>
            <span className="text-xs text-ink-2">{needYou.length} mission{needYou.length === 1 ? "" : "s"}</span>
          </div>
          {needYou.length === 0 ?
            <p className="text-sm text-ink-2 mt-3">{loading ? "Checking approvals…" : "Nothing needs your approval right now."}</p> :
            <div className="mt-3 space-y-2">{needYou.slice(0,4).map(m =>
              <button type="button" key={m.mission_id} onClick={() => navigate("/mission?id=" + encodeURIComponent(m.mission_id))}
                className="w-full rounded-xl border border-brand/30 bg-brand/10 p-3 text-left flex items-start justify-between gap-3">
                <div><p className="text-sm font-semibold text-ink">{short(m.goal)}</p><p className="text-xs text-brand-light mt-1">{statusLabel(m.state)} · Review what EARNOS needs</p></div>
                <ArrowRight size={17} className="shrink-0 text-brand-light"/>
              </button>
            )}</div>}
        </Card>
        <Card>
          <div className="flex justify-between items-center gap-2">
            <h2 className="text-lg font-bold text-ink">Your recent work</h2>
            <button type="button" onClick={() => navigate("/mission")} className="text-xs font-bold text-brand-light">All missions</button>
          </div>
          {recent.length === 0 ?
            <p className="text-sm text-ink-2 mt-3">{loading ? "Loading mission history…" : "No missions yet. Choose a goal above to begin."}</p> :
            <div className="mt-3 space-y-2">{recent.map(m =>
              <button type="button" key={m.mission_id} onClick={() => navigate("/mission?id=" + encodeURIComponent(m.mission_id))}
                className="w-full rounded-xl border border-line p-3 text-left flex items-center justify-between gap-3 hover:border-brand">
                <div className="min-w-0"><p className="text-sm text-ink font-semibold truncate">{m.goal}</p><p className="text-xs text-ink-2 mt-1 capitalize">{statusLabel(m.state)} · {statusLabel(m.stage)}</p></div>
                {m.state === "completed" ? <CheckCircle2 size={17} className="shrink-0 text-ok"/> : <Clock3 size={17} className="shrink-0 text-ink-2"/>}
              </button>)}</div>}
        </Card>
      </div>
      {move ? <Card testID="today-move-card">
        <div className="flex flex-wrap justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="uppercase text-xs font-bold text-brand-light tracking-wider">Your next Money Move</p>
            <h2 className="text-lg font-bold text-ink mt-2">{move.title}</h2>
            <p className="text-sm text-ink-2 mt-1">{move.why}</p>
          </div>
          <button type="button" data-testid="start-money-move" onClick={() => navigate("/move/" + encodeURIComponent(move.move_id))}
            className="rounded-xl bg-brand text-white font-bold px-5 py-3 flex items-center gap-2">Start move <ArrowRight size={16}/></button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" data-testid="ask-earnos" onClick={() => navigate("/chat")} className="text-sm font-semibold text-ink-2 border border-line rounded-xl p-2">Ask EARNOS</button>
          <button type="button" data-testid="save-later" onClick={() => navigate("/moves")} className="text-sm font-semibold text-ink-2 border border-line rounded-xl p-2">See all moves</button>
        </div>
      </Card> : null}
      <p className="text-xs text-ink-2 text-center">EARNOS is built to help complete lawful goals, not promise instant wealth. A verified file, a launched offer, and a real payment are different milestones.</p>
    </div>
  );
}

